/**
 * Client-side validation for collection query filters and where clauses.
 * Aligns with backend rules (e.g. empty text filter values rejected).
 */

const API_PREFIX = '[Collection API]';

export type LegacyFilter = {
    field: string;
    operator?: string;
    value: unknown;
};

export type PredicateClause = {
    fieldName?: string;
    operator?: string;
    value?: unknown;
};

/** Nested where group or leaf predicate (flexible query shape). */
export type WhereClauseNode = PredicateClause & {
    op?: string;
    clauses?: WhereClauseNode[];
};

export function isFilterValueEmpty(value: unknown): boolean {
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === 'string') {
        return value.trim() === '';
    }
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return true;
        }
        return value.every((item) => isFilterValueEmpty(item));
    }
    return false;
}

export function validateLegacyFilter(filter: LegacyFilter): string | null {
    const fieldName = filter.field;
    const value = filter.value;
    if (isFilterValueEmpty(value)) {
        return `Filter value for field '${fieldName}' cannot be empty`;
    }
    if (filter.operator?.toUpperCase() === 'BTWN' && Array.isArray(value)) {
        return value.length !== 2 ||
            isFilterValueEmpty(value[0]) ||
            isFilterValueEmpty(value[1])
            ? `Filter value for field '${fieldName}' cannot be empty`
            : null;
    }
    return null;
}

export function validatePredicateClause(
    clause: PredicateClause
): string | null {
    if (!clause.fieldName && !clause.operator && !clause.value) {
        return null;
    }
    const fieldName = clause.fieldName;
    const value = clause.value;
    if (isFilterValueEmpty(value)) {
        return `Filter value for field '${fieldName}' cannot be empty`;
    }
    if (clause.operator?.toUpperCase() === 'BTWN' && Array.isArray(value)) {
        return value.length !== 2 ||
            isFilterValueEmpty(value[0]) ||
            isFilterValueEmpty(value[1])
            ? `Filter value for field '${fieldName}' cannot be empty`
            : null;
    }
    return null;
}

export function collectWhereClauseErrors(where: WhereClauseNode): string[] {
    const errors: string[] = [];
    if (!where.clauses) {
        return errors;
    }
    for (const clause of where.clauses) {
        if (clause.clauses) {
            errors.push(...collectWhereClauseErrors(clause));
        } else {
            const err = validatePredicateClause(clause);
            if (err) errors.push(err);
        }
    }
    return errors;
}

export type ValidateQueryFiltersInput = {
    filters?: LegacyFilter[];
    where?: WhereClauseNode;
};

export function validateQueryFilters({
    filters,
    where,
}: ValidateQueryFiltersInput): void {
    const errors: string[] = [];
    if (filters?.length) {
        for (const filter of filters) {
            const err = validateLegacyFilter(filter);
            if (err) errors.push(err);
        }
    }
    if (where) {
        errors.push(...collectWhereClauseErrors(where));
    }
    if (errors.length > 0) {
        throw new Error(
            `${API_PREFIX} Filter validation failed with: ${
                errors.length
            } errors; errors are: [${errors.join(', ')}]`
        );
    }
}
